import type { EntityQueryTarget } from "@/Boards/definition/entityColumns";
import { useAuth } from "@/providers/auth";
import { isObjectRecord } from "@/shared/domain/boards";
import { CUSTOM_FIELD_ENTITY_BY_TARGET } from "@/shared/domain/boards/entityFieldNames";
import { useReadyCustomFieldsByEntity } from "@/shared/domain/boards/hooks";

import type { CustomFieldsOfCatalog } from "./boardConfirmationText";

/**
 * The org's Ready custom fields of each catalog the controls of
 * `definitions` name a field of: the catalog a value control takes its
 * choices from (`domain.kind` is `entity-field`) and the catalog a field
 * control offers the fields of (`target`). The cards name those fields with
 * them (`entityFieldDisplayName`), as the control bar does. A catalog with no
 * custom fields gives an empty list, and one whose list has not arrived
 * gives `undefined`. Each definition is the model's raw JSON, so a stage
 * list, stage, body or target of the wrong type names no catalog. Only the
 * lists of catalogs that can have custom fields
 * (`CUSTOM_FIELD_ENTITY_BY_TARGET`) are requested.
 */
export function useControlCustomFields(
  ...definitions: readonly unknown[]
): CustomFieldsOfCatalog {
  const { currentOrganization } = useAuth();
  const listsByEntity = useReadyCustomFieldsByEntity(
    controlCatalogsOf(definitions),
    currentOrganization?.org_id,
  );
  return (target) => {
    const entityType = CUSTOM_FIELD_ENTITY_BY_TARGET[target];
    return entityType === undefined ? [] : listsByEntity[entityType];
  };
}

/** The catalogs with custom fields that a control in `definitions` names a
 *  field of. */
function controlCatalogsOf(
  definitions: readonly unknown[],
): Set<EntityQueryTarget> {
  const targets = new Set<EntityQueryTarget>();
  for (const definition of definitions) {
    const stages =
      isObjectRecord(definition) && Array.isArray(definition.stages)
        ? definition.stages
        : [];
    for (const stage of stages) {
      const target = controlCatalogOf(stage);
      if (
        target !== undefined &&
        Object.hasOwn(CUSTOM_FIELD_ENTITY_BY_TARGET, target)
      ) {
        targets.add(target as EntityQueryTarget);
      }
    }
  }
  return targets;
}

/** The catalog a control names a field of, when the stage is such a
 *  control. */
function controlCatalogOf(stage: unknown): string | undefined {
  const body =
    isObjectRecord(stage) && isObjectRecord(stage.body)
      ? stage.body
      : undefined;
  if (body?.kind === "field-control") {
    return typeof body.target === "string" ? body.target : undefined;
  }
  if (
    body?.kind === "value-control" &&
    isObjectRecord(body.domain) &&
    body.domain.kind === "entity-field"
  ) {
    return typeof body.domain.target === "string"
      ? body.domain.target
      : undefined;
  }
  return undefined;
}
